import { useEffect, useRef } from 'react'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Skeleton from '@mui/material/Skeleton'
import Typography from '@mui/material/Typography'
import ArrowForward from '@mui/icons-material/ArrowForward'
import AutoAwesome from '@mui/icons-material/AutoAwesome'
import { Theme } from '@mui/material/styles'
import type { GlobalSearchCategory } from './types'

const styles = {
  list: {
    overflowY: 'auto',
    padding: 0,
  },
  categoryHeader: {
    lineHeight: '32px',
    fontWeight: 600,
    borderRadius: 0,
  },
  categoryHeaderText: {
    fontWeight: 600,
    fontSize: '0.8125rem',
  },
  viewAllIcon: {
    fontSize: '0.875rem',
    opacity: 0.5,
  },
  empty: {
    padding: (theme: Theme) => theme.spacing(3),
    textAlign: 'center',
    opacity: 0.5,
  },
  hint: {
    padding: (theme: Theme) => theme.spacing(3),
    textAlign: 'center',
    opacity: 0.4,
  },
  skeletonItem: {
    padding: (theme: Theme) => theme.spacing(0.5, 2),
  },
}

interface GlobalSearchResultsProps {
  categories: GlobalSearchCategory[]
  selectedIndex: number
  onSelect: (navigateTo: string) => void
  assistantQuery: string
  onAskAssistant: () => void
  hasSearchTerm: boolean
  loading: boolean
}

export const GlobalSearchResults = ({
  categories,
  selectedIndex,
  onSelect,
  assistantQuery,
  onAskAssistant,
  hasSearchTerm,
  loading,
}: GlobalSearchResultsProps) => {
  const selectedRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: 'nearest' })
  }, [selectedIndex])

  if (!hasSearchTerm) {
    return <Typography sx={styles.hint}>Search for pages, inputs, outputs, appliances...</Typography>
  }

  const hasResults = categories.some((c) => c.results.length > 0) || !!assistantQuery

  if (!hasResults) {
    if (loading) {
      return (
        <List sx={styles.list}>
          <ListItem sx={styles.skeletonItem}>
            <Skeleton variant="text" width={100} />
          </ListItem>
          <ListItem sx={styles.skeletonItem}>
            <Skeleton variant="text" width="60%" height={28} />
          </ListItem>
        </List>
      )
    }
    return <Typography sx={styles.empty}>No results found</Typography>
  }

  let flatIndex = 0

  return (
    <List sx={styles.list}>
      {categories.map((category) => {
        const isNavigable = !!category.navigateTo
        const headerIndex = isNavigable ? flatIndex++ : -1
        const isHeaderSelected = isNavigable && headerIndex === selectedIndex
        const hasMore = category.total > category.results.length

        return (
          <li key={category.category}>
            <ul style={{ padding: 0 }}>
              {isNavigable ? (
                <ListItemButton
                  sx={styles.categoryHeader}
                  selected={isHeaderSelected}
                  ref={isHeaderSelected ? selectedRef : undefined}
                  onClick={() => onSelect(category.navigateTo)}
                  dense
                >
                  <ListItemText
                    primary={`${category.category} (${category.total})`}
                    slotProps={{ primary: { sx: styles.categoryHeaderText } }}
                  />
                  {hasMore && <ArrowForward sx={styles.viewAllIcon} />}
                </ListItemButton>
              ) : (
                <ListItem sx={styles.categoryHeader} dense>
                  <ListItemText
                    primary={`${category.category} (${category.total})`}
                    slotProps={{ primary: { sx: styles.categoryHeaderText } }}
                  />
                </ListItem>
              )}
              {category.results.map((result) => {
                const currentIndex = flatIndex++
                const isSelected = currentIndex === selectedIndex
                return (
                  <ListItemButton
                    key={result.id}
                    selected={isSelected}
                    ref={isSelected ? selectedRef : undefined}
                    onClick={() => onSelect(result.navigateTo)}
                    dense
                    sx={{ pl: 4 }}
                  >
                    <ListItemText primary={result.name} />
                  </ListItemButton>
                )
              })}
            </ul>
          </li>
        )
      })}
      {assistantQuery ? (
        <ListItemButton
          selected={flatIndex === selectedIndex}
          ref={flatIndex === selectedIndex ? selectedRef : undefined}
          onClick={onAskAssistant}
          dense
        >
          <AutoAwesome sx={{ mr: 2, color: 'primary.main' }} />
          <ListItemText
            primary="Ask Assistant"
            secondary={assistantQuery}
            slotProps={{ secondary: { noWrap: true } }}
          />
        </ListItemButton>
      ) : null}
    </List>
  )
}
